'use client';

import useSWRInfinite from 'swr/infinite';
import { useInitialParams } from '@/app/components/hotel-detail/InitialParamsProvider';
import { fetchWithTracingAndLogging } from '@/app/components/hotel-detail/common/fetchWithTracing';
import { getLoggingInfo } from '@/app/components/hotel-detail/lib/loggingInfo';
import { BASE_PATH } from '@/app/components/search/utils/path';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import getSearchParams from '@/app/components/search/common/getSearchParams';
import { useFetchHotelStatic } from './useFetchHotelStatic';

export const TA_REVIEWS_PER_PAGE = 20;

export function useFetchTripAdvisorReviews() {
  const { pageType } = useInitialParams();
  const { searchInfoData, localization, commonData } = usePageWrapperData();
  const { data: staticData } = useFetchHotelStatic();

  // TEMP(MPH-370): `opinionId` still holds the legacy TrustYou UUID until Supply repopulates it
  // with the TA location id. The `&ta_location_id=` URL param lets devs/reviewers force a real TA
  // id for local e2e testing (e.g. 15235805 → Riu Plaza España). Only fires when the param is
  // explicitly present, so it is inert in production. Remove once Supply lands the real ids.
  const forcedTaLocationId = getSearchParams().ta_location_id;
  const tripadvisorLocationId = forcedTaLocationId || staticData?.hotelOpinion.opinionId;

  return useSWRInfinite(
    getKey(tripadvisorLocationId, localization.locale),
    async (url) =>
      fetchWithTracingAndLogging<TripadvisorApiResponse>(
        url,
        { method: 'GET', headers: { 'content-type': 'application/json' } },
        getLoggingInfo(pageType, 'tripAdvisorReviews', 'TripAdvisorReviewsSection'),
        searchInfoData.searchTraceParent,
        commonData
      ),
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      revalidateFirstPage: false,
      shouldRetryOnError: false,
    }
  );
}

function getKey(tripadvisorLocationId: string | undefined, locale: string) {
  return (pageIndex: number, previousPageData: TripadvisorApiResponse | null) => {
    if (!tripadvisorLocationId) return null;
    if (previousPageData) {
      const totalPages = previousPageData.data.pagination?.total_pages;
      // If pagination is present, use it; otherwise fall back to review-count heuristic.
      if (totalPages !== undefined && pageIndex >= totalPages) return null;
      if (totalPages === undefined && (previousPageData.data.reviews?.length ?? 0) < TA_REVIEWS_PER_PAGE) return null;
    }

    const params = new URLSearchParams({
      hotelId: tripadvisorLocationId,
      locale,
      page: String(pageIndex + 1),
      size: String(TA_REVIEWS_PER_PAGE),
    });

    return `${BASE_PATH}/api/hotel-detail/tripadvisor-reviews?${params.toString()}`;
  };
}
